import { Box, makeStyles } from '@material-ui/core';
import { queueChatMessage } from '@reducers/chatMessageQueue';
import {
  QuickMessageFilesFormatType,
  QuickMessageMediaFiles,
  QuickMessageType,
} from '@src/components/AdminHome/QuickMessages/types';
import { MediaType } from '@src/model/frontendmodel';
import { setShowQuickMsg } from '@src/reducers/activeChatQuickMessages';
import { changeWrittenMessage } from '@src/reducers/chats';
import { enqueueQuickMessageFiles } from '@src/reducers/quickMessages';
import { activeChatQuickMessagesSelector } from '@src/selectors/activeChatQuickMessages';
import { activeChatSelector } from '@src/selectors/chats';
import { QuickMessage } from '@src/types';
import StorageUtils from '@src/utils/browserstorage';
import React, { RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LINE_HEIGHT } from '../ComposerTextInput/index';
import QuickMessaFilesPreview from './QuickMessaFilesPreview';
import QuickMessageList from './QuickMessageList';

const PADDING = 65;
const MIN_BOTTOM = 100;

const QuickMessages: React.FC<{
  textInputRef: RefObject<HTMLDivElement | null>;
  command: string;
  setCommandQuickMessage: (command: string) => void;
}> = ({ textInputRef, command, setCommandQuickMessage }) => {
  const dispatch = useDispatch();

  const { root } = useStyles();

  const activeChat = useSelector(activeChatSelector);
  const { showQuickMsg } = useSelector(activeChatQuickMessagesSelector);

  const [maxRows, setMaxRows] = React.useState(1);

  const quickMessageSelected = (message: QuickMessage) => {
    const { type, location } = message;

    if (type === QuickMessageType.LOCATION && location) {
      dispatch(
        queueChatMessage(
          { type: MediaType.LOCATION, ...location },
          activeChat?.id,
          activeChat?.provider
        )
      );
    }

    if (type === QuickMessageType.FLOW) {
      const { flow_data: flowData } = message;
      if (flowData) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { waba_id: wabaId, ...rest } = flowData;

        dispatch(
          queueChatMessage(
            { type: MediaType.FLOW, ...rest },
            activeChat?.id,
            activeChat?.provider
          )
        );
      } else {
        // eslint-disable-next-line no-console
        console.error('flow_data is missing!\n', message);
      }
    }

    if (type === QuickMessageType.FILES) {
      const { fileDetails } = message.files as QuickMessageMediaFiles;

      dispatch(
        enqueueQuickMessageFiles(
          fileDetails.map(file => ({
            type:
              file.format === QuickMessageFilesFormatType.PDF
                ? MediaType.FILE
                : MediaType.IMAGE,
            file: new File([], ''),
            url: file.fileUrl,
          }))
        )
      );
    }

    dispatch(
      changeWrittenMessage({
        chatId: activeChat?.id,
        message: message.message || '',
      })
    );

    setCommandQuickMessage('');

    dispatch(setShowQuickMsg(false));
    textInputRef?.current?.focus();
  };

  useEffect(() => {
    setMaxRows(
      Number(StorageUtils.getFromLocalStorage('agentChatComposeTextRows')) || 1
    );
  }, [showQuickMsg]);

  useEffect(() => {
    setCommandQuickMessage('');
    dispatch(setShowQuickMsg(false));
  }, [activeChat.id, dispatch, setCommandQuickMessage]);

  return (
    <>
      <Box
        className={root}
        style={{
          bottom: `${Math.max(maxRows * LINE_HEIGHT + PADDING, MIN_BOTTOM)}px`,
        }}
      >
        {activeChat && (
          <QuickMessageList
            activeChat={activeChat}
            command={command}
            onMessageSelected={quickMessageSelected}
          />
        )}
      </Box>
      <QuickMessaFilesPreview anchorRef={textInputRef} />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: 1000,
    position: 'absolute',
    left: '14px',
    right: 0,
    width: '96%',
    backgroundColor:
      theme.palette.type === 'dark' ? '#1E2128' : theme.palette.grey[100],
    borderRadius: '8px',
    boxShadow:
      theme.palette.type === 'dark'
        ? '0px 16px 24px 0px rgba(0,0,0,0.5)'
        : '0px 16px 24px 0px #0000003d',
  },
}));

export default QuickMessages;
